import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Chip,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type {
  SubscriptionResponse,
  VisitBalanceResponse,
} from "@bw/shared/types/generated";
import { useEntitlementUsage } from "../hooks/useEntitlementUsage";
import { planTypeDisplay } from "../lib/planDisplay";
import {
  deriveEntitlementUsageView,
  deriveSubscriptionView,
  deriveVisitBalanceView,
} from "../lib/subscriptionState";

// Included-classes usage (Slice 6.2) is only meaningful for a RECURRING plan
// (monthly/quarterly/annual) — a visits_card pass uses the visit balance above
// instead. Only "classes" is queried here; other entitlement services are a
// future extension (see membership-plans.tsx on the business side).
const RECURRING_PLAN_TYPES = new Set(["monthly", "quarterly", "annual"]);
const ENTITLEMENT_SERVICE_CLASSES = "classes";

/**
 * One My-Memberships list row. Renders the enriched SubscriptionResponse
 * verbatim: plan_name (em-dash fallback — never a raw id), the derived honest
 * status line, the price + cadence, and — for a visits_card pass — the joined
 * visit balance, or — for a recurring plan — its included-classes usage this
 * period. The whole row links into the membership detail.
 *
 * The status label/detail comes from deriveSubscriptionView (a pure mapping of
 * the server status + lifecycle flags). Nothing is computed — no proration, no
 * fabricated renewal. Included-classes usage comes verbatim from
 * GET .../entitlements/classes/usage; a plan with no such entitlement shows
 * nothing (deriveEntitlementUsageView returns null for total=0).
 */
export function MembershipRow({
  subscription,
  balance,
}: {
  subscription: SubscriptionResponse;
  balance?: VisitBalanceResponse;
}) {
  const view = deriveSubscriptionView(subscription);
  const display = subscription.plan_type
    ? planTypeDisplay(subscription.plan_type)
    : null;
  const isPass = display?.framing === "pass";
  const balanceView =
    isPass && balance ? deriveVisitBalanceView(balance) : null;

  const isRecurring = Boolean(
    subscription.plan_type && RECURRING_PLAN_TYPES.has(subscription.plan_type),
  );
  const usageQ = useEntitlementUsage(
    subscription.subscription_id,
    ENTITLEMENT_SERVICE_CLASSES,
    isRecurring,
  );
  const usageView = usageQ.data
    ? deriveEntitlementUsageView(usageQ.data, display?.intervalNoun ?? null)
    : null;

  // plan_name is enriched server-side; a null (unresolved) plan degrades to an
  // em-dash, NEVER the raw plan_id.
  const planName = subscription.plan_name?.trim() || "—";
  const facilityName = subscription.facility_name?.trim() || null;

  return (
    <ListItem component="li" disablePadding divider>
      <ListItemButton
        component={RouterLink}
        to={`/me/memberships/${subscription.subscription_id}`}
        sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ width: "100%", justifyContent: "space-between" }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{planName}</Typography>
            {facilityName ? (
              <Typography variant="body2" color="text.secondary">
                {facilityName}
              </Typography>
            ) : null}
          </Box>

          <Stack spacing={0.5} sx={{ alignItems: { sm: "flex-end" } }}>
            <Chip
              size="small"
              variant={view.tone === "active" ? "filled" : "outlined"}
              color={toneColor(view.tone)}
              label={view.label}
            />
            {view.detail ? (
              <Typography variant="body2" color="text.secondary">
                {view.detail}
              </Typography>
            ) : null}
            {balanceView ? (
              <Typography
                variant="body2"
                color={balanceView.usable ? "text.primary" : "text.secondary"}
              >
                {balanceView.label}
              </Typography>
            ) : null}
            {usageView ? (
              <Typography variant="body2" color="text.secondary">
                {usageView.label}
              </Typography>
            ) : null}
            {subscription.price != null ? (
              <Typography variant="body2" color="text.secondary">
                {formatMoney(subscription.price)}
                {display?.priceSuffix ?? ""}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
}

function toneColor(
  tone: ReturnType<typeof deriveSubscriptionView>["tone"],
): "success" | "warning" | "error" | "default" {
  switch (tone) {
    case "active":
      return "success";
    case "ending":
    case "suspended":
      return "warning";
    case "attention":
      return "error";
    default:
      return "default";
  }
}
